// Copyright (c) 2025 Audere, All Rights Reserved

import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  MenuItem,
  Paper,
  Select,
  Switch,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { useSelector } from "react-redux";
import { createStyleSheet } from "../utils/styles";
import TuningIcon from "../../assets/further_tuning.svg";
import { selectCurrentName, selectCurrentPicture } from "../features/auth/authSlice";
import { getFirstChar, getRDTName } from "../utils/string";
import { useTranslation } from "react-i18next";
import { theme } from "../../theme";
import FurtherTuningDialog from "./FurtherTuningDialog";
import dayjs from "dayjs";
import type { ReportsHeaderProps } from "../types/projects";
import { POSITIVE_LABEL } from "../utils/constants";
import { useUpdateRDTMutation } from "../features/data/projectApiSlice";
import AllowSharingDialog from "./AllowSharingDialog";

const ReportsHeader = (props: ReportsHeaderProps) => {
  const {
    selectedTest,
    outcomeDistribution,
    dropdownOptions,
    setSelectedOption,
    selectedOption,
    handleRDTSelection,
  } = props;
  const { t } = useTranslation("ReportsHeader");
  const picture = useSelector(selectCurrentPicture);
  const userName = useSelector(selectCurrentName);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTab = useMediaQuery(theme.breakpoints.down("md"));

  const [isFurtherTuningOpen, setIsFurtherTuningOpen] = useState(false);
  const [isAllowSharingOpen, setIsAllowSharingOpen] = useState(false);
  const handleClose = () => {
    setIsFurtherTuningOpen(false);
  };

  const openFurtherTuningDialog = () => {
    setIsFurtherTuningOpen(true);
  };

  const [updateRDT] = useUpdateRDTMutation();
  const [isToggle, setIsToggle] = useState<boolean>(false);

  const handleAllowSharing = () => {
    const data = {
      rdt_uid: selectedTest.rdt.rdt_uid,
      allow_sharing: true,
    };
    updateRDT(data).then(response => {
      if (response?.data?.rdt_uid) {
        handleRDTSelection(response.data.rdt_uid);
      }
    });
    setIsAllowSharingOpen(false);
  };

  useEffect(() => {
    setIsToggle(selectedTest.rdt.allow_sharing);
  }, [selectedTest.rdt.allow_sharing]);

  return (
    <Box sx={styles.container}>
      <Paper elevation={2} sx={styles.testDetails}>
        <Grid container spacing={isMobile ? 2 : 4} justifyContent="space-between">
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ ...styles.leftBox, ...(isTab && { alignItems: "center" }) }}>
              <Typography variant="body2" color="text.secondary">
                {selectedTest?.name}
              </Typography>
              <Box sx={styles.leftBoxContainer}>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  {getRDTName(selectedTest.rdt)}{" "}
                  {selectedTest?.models?.[0].model_version && (
                    <Chip
                      label={t("version", { version: selectedTest?.models?.[0].model_version })}
                      variant="outlined"
                      sx={styles.chip}
                    />
                  )}
                </Typography>
                <Select
                  value={selectedOption}
                  onChange={e => setSelectedOption(e.target.value)}
                  size="small"
                  sx={styles.dropdown}
                  data-testid="dropdown"
                >
                  {dropdownOptions.map((option: { label: string; value: string }) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <Box sx={styles.metaInfoContainer}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t("lastModified")}
                  </Typography>
                  <Typography variant="body2" fontWeight="medium" color="text.secondary">
                    {dayjs(selectedTest.rdt.last_modified).fromNow()}
                  </Typography>
                </Box>

                {!isTab && <Divider orientation="vertical" flexItem sx={styles.divider} />}
                <Box pr={2} mt={2}>
                  <Box sx={styles.submitterInfo}>
                    <Typography variant="body2" color="text.secondary">
                      {t("submitter")}:
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" color="text.secondary">
                      {userName}
                    </Typography>
                  </Box>
                  {picture ? (
                    <Box component="img" src={picture} crossOrigin="anonymous" sx={styles.avatar} />
                  ) : (
                    <Avatar>{getFirstChar(userName)}</Avatar>
                  )}
                </Box>

                {!isTab && <Divider orientation="vertical" flexItem sx={styles.divider} />}
              </Box>
            </Box>
          </Grid>
          <Grid
            size={{ xs: 12, md: 6 }}
            sx={{ ...styles.rightBox, ...(isTab && { alignItems: "normal" }) }}
          >
            <Box sx={styles.switchContainer}>
              <Typography variant="body2" textAlign={isMobile ? "left" : "right"}>
                {t("photoSetToggleText")}
              </Typography>
              <Switch
                slotProps={{ input: { role: "switch" } }}
                checked={isToggle}
                disabled={isToggle}
                onChange={() => {
                  if (!isToggle) {
                    setIsAllowSharingOpen(true);
                  }
                }}
                color="default"
              />
            </Box>

            <Button
              variant="outlined"
              endIcon={<Box component="img" src={TuningIcon} alt="Tuning" sx={styles.tuningIcon} />}
              sx={styles.tuningBtn}
              onClick={openFurtherTuningDialog}
            >
              {t("furtherTuning")}
            </Button>
          </Grid>
        </Grid>
      </Paper>
      <Paper elevation={2} sx={styles.testOutcomeReport}>
        <Grid container alignItems="center" justifyContent="space-evenly">
          {outcomeDistribution
            .slice()
            .sort((a, b) => (a.label === POSITIVE_LABEL ? -1 : b.label === POSITIVE_LABEL ? 1 : 0))
            .map((item, index) => (
              <React.Fragment key={item.label}>
                <Grid
                  container
                  direction="column"
                  alignItems="center"
                  justifyContent="center"
                  sx={styles.testReportContainer}
                >
                  <Typography
                    variant="h3"
                    fontWeight="bold"
                    color={
                      item.label === POSITIVE_LABEL
                        ? theme.palette.primary.main
                        : theme.palette.grey[900]
                    }
                  >
                    {item.percentage}
                  </Typography>
                  <Button
                    variant="contained"
                    sx={{
                      ...styles.statusButton,
                      background:
                        item.label === POSITIVE_LABEL
                          ? theme.palette.primary.main
                          : theme.palette.grey[900],
                    }}
                  >
                    {item.label}
                  </Button>
                </Grid>
                {!isTab && index < outcomeDistribution.length - 1 && (
                  <Divider orientation="vertical" flexItem />
                )}
              </React.Fragment>
            ))}
        </Grid>
      </Paper>
      <FurtherTuningDialog
        projectName={selectedTest?.name}
        isOpen={isFurtherTuningOpen}
        onClose={handleClose}
      />
      <AllowSharingDialog
        open={isAllowSharingOpen}
        onClose={() => {
          setIsAllowSharingOpen(false);
        }}
        onHandleClick={handleAllowSharing}
      />
    </Box>
  );
};

export default ReportsHeader;

const styles = createStyleSheet({
  container: {
    borderRadius: "4px",
    width: "100%",
    p: 2,
  },
  testDetails: {
    p: 2,
    mb: 1,
  },
  avatar: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    objectFit: "cover",
  },
  leftBox: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  metaInfoContainer: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 2,
  },
  submitterInfo: {
    display: "flex",
    alignItems: "center",
    gap: 1,
  },
  divider: {
    mx: 1,
  },
  switchContainer: {
    display: "flex",
    alignItems: "center",
    gap: 1,
  },
  tuningIcon: {
    width: 20,
    height: 20,
  },
  tuningBtn: {
    whiteSpace: "nowrap",
    border: `1px solid ${theme.palette.grey}`,
  },
  statusButton: {
    borderRadius: 5,
    px: 3,
    py: 0.5,
    mt: 1,
  },
  chip: {
    fontSize: "14px",
    fontWeight: 400,
    border: `1px solid ${theme.palette.common.black}`,
  },
  rightBox: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    gap: 2,
    alignItems: "flex-end",
  },
  testOutcomeReport: {
    p: 2,
  },
  testReportContainer: {
    px: 2,
    py: 2,
  },
  leftBoxContainer: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },
  dropdown: {
    width: theme.spacing(12),
  },
});
